import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { PopulationData } from '../../demographicsService'

interface CommunityStabilityChartProps {
  data: PopulationData
  height?: number
  t: TFunction
}

export interface CommunityStabilityChartRef {
  exportChart: () => string | null
}

export const CommunityStabilityChart = forwardRef<
  CommunityStabilityChartRef,
  CommunityStabilityChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Prepare stability data
    const stabilityData = [
      {
        name: t('charts.communityStability.legend.0to1Year'),
        value: data.number_residents_city_0_1_year,
        category: 'newcomers',
        color: '#ef4444',
        description: t('charts.communityStability.descriptions.newcomers'),
      },
      {
        name: t('charts.communityStability.legend.1to5Years'),
        value: data.number_residents_city_1_5_year,
        category: 'settling',
        color: '#f59e0b',
        description: t('charts.communityStability.descriptions.settling'),
      },
      {
        name: t('charts.communityStability.legend.6to10Years'),
        value: data.number_residents_city_6_10_year,
        category: 'established',
        color: '#10b981',
        description: t('charts.communityStability.descriptions.established'),
      },
      {
        name: t('charts.communityStability.legend.10plusYears'),
        value: data.number_residents_city_10_plus_year,
        category: 'long_term',
        color: '#3b82f6',
        description: t('charts.communityStability.descriptions.longTerm'),
      },
      {
        name: t('charts.communityStability.legend.sinceBirth'),
        value: data.number_residents_city_since_birth,
        category: 'natives',
        color: '#8b5cf6',
        description: t('charts.communityStability.descriptions.natives'),
      },
    ]

    // Calculate percentages
    const totalPopulation = data.total_population
    const dataWithPercentages = stabilityData.map((item) => ({
      ...item,
      percentage:
        totalPopulation > 0
          ? ((item.value / totalPopulation) * 100).toFixed(1)
          : '0',
    }))

    // Get real estate implications
    const getRealEstateImplication = (category: string) => {
      switch (category) {
        case 'newcomers':
          return t('charts.communityStability.implications.newcomers')
        case 'settling':
          return t('charts.communityStability.implications.settling')
        case 'established':
          return t('charts.communityStability.implications.established')
        case 'long_term':
          return t('charts.communityStability.implications.longTerm')
        case 'natives':
          return t('charts.communityStability.implications.natives')
        default:
          return t('charts.communityStability.implications.mixed')
      }
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: function (params: any) {
          const item = params.data
          const implication = getRealEstateImplication(item.category)
          return `
            <div style="text-align: left;">
              <strong>${t('charts.communityStability.tooltips.residents')}: ${item.name}</strong><br/>
              <span style="color: ${item.color};">${t('charts.communityStability.tooltips.count')}: ${item.value.toLocaleString()}</span><br/>
              <span style="color: ${item.color};">${t('charts.communityStability.tooltips.percentage')}: ${item.percentage}%</span><br/>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <strong>${t('charts.communityStability.tooltips.marketImpact')}:</strong><br/>
                <span style="color: #6b7280;">${implication}</span>
              </div>
            </div>
          `
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        itemGap: 15,
        textStyle: {
          fontSize: 11,
        },
        formatter: function (name: string) {
          const item = dataWithPercentages.find((d) => d.name === name)
          return `${name} (${item?.percentage}%)`
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '80px',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dataWithPercentages.map((d) => d.name),
        axisLabel: {
          fontSize: 11,
          interval: 0,
          rotate: 30,
          color: '#1f2937',
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 11,
          formatter: '{value}',
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: t('charts.series.residents'),
          type: 'bar',
          data: dataWithPercentages.map((item) => ({
            name: item.name,
            value: item.value,
            category: item.category,
            percentage: item.percentage,
            description: item.description,
            itemStyle: {
              color: item.color,
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: true,
            position: 'top',
            formatter: function (params: any) {
              return `${params.data.percentage}%`
            },
            fontSize: 11,
            fontWeight: 'bold',
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '300px',
      }}
    />
  )
})

CommunityStabilityChart.displayName = 'CommunityStabilityChart'
